import { UserIdSchema } from "@core/domains/identity";
import { identity } from "@core/foundation/function";
import { ulid } from "@core/foundation/id";
import {
  createModel,
  createModelId,
  createModelSchemas,
  noModelUpdate,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { ID_PREFIX } from "./shared/id-prefix.ts";
import { TaxonomyIdSchema } from "./shared/taxonomy-id.ts";
import { TagKindIdSchema } from "./tag-kind.ts";

const { generateId: generateTagId, idSchema: TagIdSchema } = createModelId({
  digestFn: ulid,
  idPrefix: ID_PREFIX.TAG,
});

export { TagIdSchema };

export type TagId = z.infer<typeof TagIdSchema>;

export const Tag = createModel({
  create: (item, schema) => {
    return { ...schema.decode(item), id: generateTagId() };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        id: TagIdSchema,
        name: z.string(),
        principalId: UserIdSchema,
        tagKindId: TagKindIdSchema,
        taxonomyId: TaxonomyIdSchema,
      },
      {
        id: "The tag's permanent id, which a rename leaves unchanged.",
        name: "The tag's name.",
        principalId: "The id of the user the tag belongs to.",
        tagKindId: "The id of the tag kind the tag is of.",
        taxonomyId: "The id of the taxonomy the tag belongs to.",
      },
    ),
    derive: {
      create: schema => schema.omit({ id: true }),
      recovery: identity,
      update: noModelUpdate,
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface Tag extends z.infer<typeof Tag.schema> {}
